/**
 * Uplink `routing_leg_faults` (#3135 / #3139, guard-v2).
 *
 * One row is one fault instance on one physical-leg × SKU identity × scope.
 * Several kinds can sit on the same leg; a leg is retired from first-pick
 * while any `act` fault applies to the request (SKU-global faults to every
 * org except an org that chose `global_trip: own` for that signal; org faults
 * only to that org). Release is per fault.
 *
 * Kinds are the guard-v2 signal registry's `faultKind`s (`silent` = stall,
 * `broken`, `overload`, `first_content_stream`, `first_content_nonstream`,
 * `prelude_fail`). The legacy four trip reasons (probe / quality / chain /
 * jev) stay on `routing_target_probe_state`. `scope_kind` is `global`
 * (`scope_id` '') or `org` (`scope_id` = org id, 0147). `recover_probe_profile`
 * is the probe profile the fault requires to be released (0147, default
 * `short`). `fault_kind` is TEXT with a non-empty CHECK only.
 */

import {
  GUARD_PROBE_PROFILES,
  GUARD_PROBE_TRANSPORTS,
  GUARD_SIGNALS,
  type GuardProbeProfile,
  type GuardProbeTransport,
} from './guaranteed-guard-config.ts';

export const LEG_FAULT_KINDS: readonly string[] = GUARD_SIGNALS.map((spec) => spec.faultKind);

export const LEG_FAULT_MODES = ['shadow', 'act'] as const;
export type LegFaultMode = (typeof LEG_FAULT_MODES)[number];

export const LEG_FAULT_SCOPE_KINDS = ['global', 'org'] as const;
export type LegFaultScopeKind = (typeof LEG_FAULT_SCOPE_KINDS)[number];

/** Empty string is the global scope id; never null (PK). */
export const LEG_FAULT_GLOBAL_SCOPE_ID = '';

export const LEG_FAULT_RECOVER_PROBE_KINDS = GUARD_PROBE_TRANSPORTS;
export type LegFaultRecoverProbeKind = GuardProbeTransport;

export const LEG_FAULT_RECOVER_PROBE_PROFILES = GUARD_PROBE_PROFILES;
export type LegFaultRecoverProbeProfile = GuardProbeProfile;

/**
 * `target_probe_recover` / `shadow_recover` `details.source` for framework
 * faults (#3135 / #3139). Probe-wave release is `cli_probe` (details also
 * carry `probe_kind` and `probe_profile`); max-hold expiry is `expiry`; the
 * ops reset route is `ops_reset`; the fault's own trip leaving act/shadow is
 * `mode_off`.
 */
export const LEG_FAULT_RECOVER_SOURCES = ['cli_probe', 'expiry', 'ops_reset', 'mode_off'] as const;
export type LegFaultRecoverSource = (typeof LEG_FAULT_RECOVER_SOURCES)[number];
export const LEG_FAULT_RECOVER_SOURCE_CLI_PROBE = 'cli_probe' satisfies LegFaultRecoverSource;
export const LEG_FAULT_RECOVER_SOURCE_EXPIRY = 'expiry' satisfies LegFaultRecoverSource;
export const LEG_FAULT_RECOVER_SOURCE_OPS_RESET = 'ops_reset' satisfies LegFaultRecoverSource;
export const LEG_FAULT_RECOVER_SOURCE_MODE_OFF = 'mode_off' satisfies LegFaultRecoverSource;

export interface LegFaultObservationSummary {
  readonly status: number;
  readonly first_content_ms: number | null;
  readonly max_gap_ms: number;
  readonly terminal_seen: boolean;
  readonly terminal_kind: string;
  readonly output_tokens: number;
}

export interface RoutingLegFaultRecord {
  readonly region: string;
  readonly scope_kind: LegFaultScopeKind;
  readonly scope_id: string;
  readonly sku_id: string;
  readonly sku_name: string;
  readonly upstream_id: string;
  readonly account_id: string;
  readonly key_bucket_id: string;
  readonly upstream_channel_id: string;
  readonly physical_model: string;
  readonly fault_kind: string;
  readonly mode: LegFaultMode;
  readonly tripped_at: string;
  readonly clean_waves: number;
  readonly last_wave_at: string | null;
  readonly last_observation: LegFaultObservationSummary | null;
  readonly recover_probe_kind: string;
  readonly recover_probe_profile: LegFaultRecoverProbeProfile;
  readonly required_clean_waves: number;
  readonly expires_at: string | null;
  readonly updated_at: string;
}

export function isLegFaultMode(value: unknown): value is LegFaultMode {
  return value === 'shadow' || value === 'act';
}

export function isLegFaultScopeKind(value: unknown): value is LegFaultScopeKind {
  return value === 'global' || value === 'org';
}
